import React from "react";
import { Helmet } from "react-helmet";
import {
  absoluteUrl,
  DEFAULT_OG_IMAGE,
  isDefaultOgImage,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  SITE_NAME,
} from "src/utils/seoSchema";
import { useLocale } from "src/i18n/LocaleProvider";
import { LOCALES, AppLocale, DEFAULT_LOCALE } from "src/i18n/localesConfig";
import { switchLocalePath } from "src/i18n/paths";
import { useLocation } from "react-router";

type SeoHeadProps = {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  ogType?: "website" | "article";
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  publishedTime?: string;
  modifiedTime?: string;
  articleTags?: string[];
  alternateRssHref?: string;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  /** Disable hreflang when page has no locale alternates (e.g. English-only blog). */
  skipHreflang?: boolean;
  robots?: string;
};

const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  canonical,
  keywords,
  ogType = "website",
  ogTitle,
  ogDescription,
  ogImage,
  twitterTitle,
  twitterDescription,
  twitterImage,
  publishedTime,
  modifiedTime,
  articleTags,
  alternateRssHref,
  jsonLd,
  skipHreflang = false,
  robots,
}) => {
  const { config, locale } = useLocale();
  const location = useLocation();
  const canonicalUrl = absoluteUrl(canonical);
  const image = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : absoluteUrl(ogImage)
    : DEFAULT_OG_IMAGE;
  const resolvedOgTitle = ogTitle || title;
  const resolvedOgDescription = ogDescription || description;
  const resolvedTwitterTitle = twitterTitle || resolvedOgTitle;
  const resolvedTwitterDescription =
    twitterDescription || resolvedOgDescription;
  const resolvedTwitterImage = twitterImage
    ? twitterImage.startsWith("http")
      ? twitterImage
      : absoluteUrl(twitterImage)
    : image;

  const schemas = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  const hreflangPath = location.pathname || canonical;

  return (
    <Helmet>
      <html lang={config.htmlLang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {robots ? <meta name="robots" content={robots} /> : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <link rel="canonical" href={canonicalUrl} />
      {alternateRssHref ? (
        <link
          rel="alternate"
          type="application/rss+xml"
          title="STR Monitoring Blog"
          href={absoluteUrl(alternateRssHref)}
        />
      ) : null}

      {!skipHreflang
        ? (Object.keys(LOCALES) as AppLocale[]).map((code) => {
            const href = absoluteUrl(switchLocalePath(hreflangPath, code));
            return (
              <link
                key={code}
                rel="alternate"
                hrefLang={LOCALES[code].hreflang}
                href={href}
              />
            );
          })
        : null}
      {!skipHreflang ? (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={absoluteUrl(switchLocalePath(hreflangPath, DEFAULT_LOCALE))}
        />
      ) : null}

      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={config.ogLocale} />
      {(Object.keys(LOCALES) as AppLocale[])
        .filter((code) => code !== locale)
        .map((code) => (
          <meta
            key={`og-alt-${code}`}
            property="og:locale:alternate"
            content={LOCALES[code].ogLocale}
          />
        ))}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:image" content={image} />
      {isDefaultOgImage(image) ? (
        <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
      ) : null}
      {isDefaultOgImage(image) ? (
        <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />
      ) : null}
      <meta property="og:url" content={canonicalUrl} />
      {ogType === "article" && publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {ogType === "article" && (modifiedTime || publishedTime) ? (
        <meta
          property="article:modified_time"
          content={modifiedTime || publishedTime}
        />
      ) : null}
      {ogType === "article" && articleTags?.length
        ? articleTags.map((tag) => (
            <meta key={`article-tag-${tag}`} property="article:tag" content={tag} />
          ))
        : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTwitterTitle} />
      <meta name="twitter:description" content={resolvedTwitterDescription} />
      <meta name="twitter:image" content={resolvedTwitterImage} />

      {schemas.map((schema, index) => (
        <script key={`ld-json-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SeoHead;
